/**
 * Discovery without a hardcoded hero (canonical spec rule 4). The featured slot is chosen from
 * the catalog: the most dramatic recently finished game (overtime, then the closest margin, then
 * the most recent), else the closest live game, else tonight's next tip. Classics — including the Hawks–Knicks acceptance
 * replay — live in their own row and are never the default.
 */
import type { FeedCard } from './feed';
import { currentWeek } from './personal';

const margin = (c: FeedCard) => (c.away.score !== undefined && c.home.score !== undefined ? Math.abs(c.away.score - c.home.score) : Number.POSITIVE_INFINITY);
/** Overtime periods from the status line ("Final/OT", "Final/2OT"); 0 when none. */
export function overtimes(c: Pick<FeedCard, 'status'>): number {
  const m = /(\d*)\s*OT\b/i.exec(c.status ?? '');
  return m ? Math.max(1, Number(m[1] || 1)) : 0;
}

/** Higher is more dramatic. Pure and unit-tested. */
export function drama(c: FeedCard): number {
  const m = margin(c);
  return overtimes(c) * 100 + (Number.isFinite(m) ? Math.max(0, 30 - m) : 0);
}

export function featuredGame(cards: readonly FeedCard[], now = Date.now()): FeedCard | undefined {
  const week = currentWeek(cards, now);
  const byDrama = (a: FeedCard, b: FeedCard) => drama(b) - drama(a) || (b.startUtc ?? '').localeCompare(a.startUtc ?? '') || a.gameId.localeCompare(b.gameId);
  const final = week.filter((c) => c.kind === 'final' && Number.isFinite(margin(c))).sort(byDrama)[0];
  if (final) return final;
  return week.filter((c) => c.kind === 'live').sort(byDrama)[0]
    ?? week.filter((c) => c.kind === 'tonight').sort((a, b) => (a.startUtc ?? '').localeCompare(b.startUtc ?? ''))[0];
}

export type DiscoveryRow = { id: 'live' | 'tonight' | 'finals' | 'following' | 'classics'; cards: FeedCard[] };

/** The five discovery rows from current-week cards + classics; empty rows are dropped. The featured game is not repeated. */
export function discoveryRows(o: { live: readonly FeedCard[]; classics: readonly FeedCard[]; followed?: readonly string[]; featuredId?: string }): DiscoveryRow[] {
  const week = o.live.filter((c) => c.gameId !== o.featuredId);
  const follows = new Set(o.followed ?? []);
  const mine = (c: FeedCard) => follows.has(c.away.tricode) || follows.has(c.home.tricode);
  const recent = (a: FeedCard, b: FeedCard) => (b.startUtc ?? '').localeCompare(a.startUtc ?? '');
  const rows: DiscoveryRow[] = [
    { id: 'live', cards: week.filter((c) => c.kind === 'live').sort((a, b) => drama(b) - drama(a)) },
    { id: 'tonight', cards: week.filter((c) => c.kind === 'tonight').sort((a, b) => (a.startUtc ?? '').localeCompare(b.startUtc ?? '')) },
    { id: 'finals', cards: week.filter((c) => c.kind === 'final').sort(recent) },
    { id: 'following', cards: follows.size ? [...week.filter(mine), ...o.classics.filter(mine)] : [] },
    { id: 'classics', cards: [...o.classics] },
  ];
  return rows.filter((r) => r.cards.length);
}
